import { GasState, ObjectCategory } from "@common/constants";
import { Numeric } from "@common/utils/math";
import { Vec } from "@common/utils/vector";
import { Game } from "../game";
import { GasManager } from "./gasManager";
import { inGrenadeGas, showVisor } from "./gasMaskLogic";

/**
 * In gas with a gas mask on: you see everything (grenade gas is invisible to you), and a light visor frame round the
 * edges of the screen, with a small breathing mask, shows you're still in it. Without a mask the war look darkens the
 * view the same way for grenade gas as for the zone's gas.
 */
class GasMaskViewClass {
    /** the player on screen is standing in grenade gas */
    inGrenadeGas = false;
    private _element?: HTMLElement;
    private _shown = false;

    /** checked ten times a second, not every frame (the mask view fades in over half a second anyway) */
    private _nextCheck = 0;

    update(): void {
        const now = performance.now();
        if (now < this._nextCheck) return;
        this._nextCheck = now + 100;
        const player = Game.activePlayer;
        const alive = player !== undefined && !player.dead;
        this.inGrenadeGas = alive && inGrenadeGas(player.position, (function* () {
            for (const particle of Game.objects.getCategory(ObjectCategory.SyncedParticle)) {
                yield { idString: particle.definition.idString, position: particle.position, scale: particle.container.scale.x };
            }
        })());
        const masked = alive && player.equipment.mask !== undefined;
        this._show(showVisor(masked, this.inGrenadeGas, alive && this._inZoneGas(player.position)));
    }

    private _inZoneGas(position: { x: number, y: number }): boolean {
        if (GasManager.state === GasState.Inactive || GasManager.radius >= 5000) return false;
        let center = GasManager.position, radius = GasManager.radius;
        if (GasManager.state === GasState.Advancing) {
            const t = Numeric.clamp((Date.now() - GasManager.lastUpdateTime) / Game.serverDt, 0, 1);
            center = Vec.lerp(GasManager.lastPosition, GasManager.position, t);
            radius = Numeric.lerp(GasManager.lastRadius, GasManager.radius, t);
        }
        return Vec.len(Vec.sub(position, center)) > radius;
    }

    private _show(on: boolean): void {
        if (on === this._shown) return;
        this._shown = on;
        if (!this._element) {
            const element = document.createElement("div");
            element.id = "gas-mask-view";
            element.setAttribute("aria-hidden", "true");
            element.innerHTML = '<div class="gmv-rim"></div><img class="gmv-icon" src="./img/game/loot/gas_mask.svg" alt="">';
            const hud = document.getElementById("game-ui");
            if (hud) hud.prepend(element);
            else document.body.appendChild(element);
            this._element = element;
        }
        this._element.classList.toggle("on", on);
    }

    /** a new game, or the view rewound */
    reset(): void {
        this.inGrenadeGas = false;
        this._nextCheck = 0;
        this._show(false);
    }
}

export const GasMaskView = new GasMaskViewClass();
